import { Form, type FormItemProps } from 'antd';
import type { RuleObject } from 'antd/es/form';
import isNil from 'lodash/isNil';
import React, {
  ReactElement,
  ReactNode,
  useCallback,
  useContext,
  useMemo
} from 'react';
import { useEffectOnce } from 'react-use';

import { FormContext } from 'components/composites/DefaultForm';
import FieldValue from 'components/forms/FieldValue';

export function convertValueToNullOnFalsy(value: unknown) {
  return value || null;
}

export interface DefaultFormItemProps
  extends Pick<
    FormItemProps,
    | 'className'
    | 'dependencies'
    | 'name'
    | 'style'
    | 'label'
    | 'hidden'
    | 'children'
    | 'rules'
    | 'initialValue'
    | 'fieldKey'
    | 'requiredMark'
    | 'normalize'
    | 'noStyle'
    | 'hasFeedback'
    | 'validateFirst'
    | 'validateTrigger'
  > {
  /**
   * Use this prop if you want to hide the 'optional' text.
   * The required condition itself is enforced by the `rules` prop.
   */
  required?: FormItemProps['required'];
  /**
   * Specify if the value should be converted to null when the form field is
   * empty
   */
  toNullOnFalsy?: boolean;
  readOnly?: boolean;
  emptyValueTextForReadOnly?: string;
  valuePropName?: string;
  labelCaption?: string | JSX.Element;
  formatReadOnlyValue?: (value: any) => ReactNode;
}

/**
 * A wrapper around the antd Form.Item with our custom config applied.
 */
function DefaultFormItem({
  className,
  dependencies,
  /** NamePath */
  name,
  style,
  label,
  labelCaption,
  hidden,
  children,
  rules,
  initialValue,
  fieldKey,
  toNullOnFalsy,
  readOnly,
  emptyValueTextForReadOnly = 'None',
  valuePropName = 'value',
  required,
  requiredMark = 'optional',
  normalize,
  noStyle,
  hasFeedback,
  validateFirst,
  validateTrigger,
  formatReadOnlyValue
}: DefaultFormItemProps): JSX.Element | null {
  useEffectOnce(() => {
    if (React.Children.count(children) > 1) {
      // eslint-disable-next-line no-console
      console.warn(
        'DefaultFormItem can only have one child otherwise the form value binding may not work'
      );
    }
  });

  const handleNormalize = useCallback(
    (value, prevValue, prevValues) => {
      if (normalize) {
        return normalize(
          toNullOnFalsy ? convertValueToNullOnFalsy(value) : value,
          prevValue,
          prevValues
        );
      }

      if (toNullOnFalsy) {
        return convertValueToNullOnFalsy(value);
      }

      return value;
    },
    [normalize, toNullOnFalsy]
  );

  const { formInstance } = useContext(FormContext);

  // Since updating Ant Design, the 'required' prop must be passed to a
  // Form.Item component in order to hide the 'optional' marking, even if a rule
  // that makes the field mandatory is given. To fix this, we will set the
  // required prop internally here if a 'required' rule is passed in as a
  // prop or required === true is set.
  const innerRequired = useMemo(
    () =>
      required ||
      (rules &&
        rules.some((rule) =>
          typeof rule === 'function' && formInstance
            ? rule(formInstance).required
            : (rule as RuleObject).required
        )),
    [required, rules, formInstance]
  );

  const labelCaptionEl = useMemo(() => {
    if (!labelCaption) {
      return undefined;
    }
    return <div className="body-muted-copy">{labelCaption}</div>;
  }, [labelCaption]);

  if (readOnly) {
    const input =
      React.Children.count(children) === 1
        ? (React.Children.only(children) as ReactElement)
        : null;
    const { suffix } = input?.props ?? {};

    return (
      <Form.Item
        label={label && <span className="body-emphasis-copy">{label}</span>}
        required={innerRequired}
        extra={labelCaptionEl}
        className={className}
        noStyle={noStyle}
      >
        <FieldValue name={name}>
          {formatReadOnlyValue ||
            ((value: any) => {
              if (!isNil(value) && suffix) return `${value} ${suffix}`;
              if (!isNil(value)) return value;
              return emptyValueTextForReadOnly;
            })}
        </FieldValue>
      </Form.Item>
    );
  }

  return (
    <Form.Item
      className={className}
      dependencies={dependencies}
      name={name}
      style={style}
      hidden={hidden}
      label={label && <span className="body-emphasis-copy">{label}</span>}
      rules={rules}
      initialValue={initialValue}
      fieldKey={fieldKey}
      normalize={handleNormalize}
      valuePropName={valuePropName}
      required={innerRequired}
      // Known issue: Pls ignore the following error "React does not recognize the `requiredMark` prop on a DOM element.
      // If you intentionally want it to appear in the DOM as a custom attribute, spell it as lowercase `requiredmark`
      // instead. If you accidentally passed it from a parent component, remove it from the DOM element.""
      requiredMark={requiredMark}
      noStyle={noStyle}
      hasFeedback={hasFeedback}
      validateFirst={validateFirst}
      validateTrigger={validateTrigger}
      // Only use extra for descriptions in between the label and the input. For other cases,
      // add them after the Form.Item. We reserve the extra prop because the Form.Item can ONLY ever have ONE child
      // and this is one method that works; otherwise the form value binding will not work.
      extra={labelCaptionEl}
    >
      {children}
    </Form.Item>
  );
}

export default DefaultFormItem;
